import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

/**
 * Custom hook for proctoring screen share with monitor detection
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.checkInterval - How often to check for monitors (ms), default 3000
 * @param {boolean} options.autoStart - Auto-start monitor detection, default true
 * @returns {Object} Hook state and functions
 */


let globalScreenStream = null;
let globalIsScreenSharing = false;
export const useScreenProctoring = (options = {}) => {
  const {
    checkInterval = 3000,
    autoStart = true,
  } = options;

  const isTabSwithingEnabled = useSelector((state)=>state.miscSlice.isTabSwitchingEnabled);

  // State
  const [multipleMonitorsDetected, setMultipleMonitorsDetected] = useState(false);
  const [monitorCount, setMonitorCount] = useState(1);
  const [permissionsStatus, setPermissionsStatus] = useState({
    screen: 'prompt' // 'prompt' | 'granted' | 'denied'
  });
  const [isScreenSharing, setIsScreenSharing] = useState(() => {
    return globalScreenStream !== null && globalScreenStream.active;
  });

  // Refs
  const monitorCheckIntervalRef = useRef(null);
  const activeStreamsRef = useRef({ screen: null });
  const screenStreamRef = useRef(null);

  /**
   * Detects multiple monitors using Screen Detection API
   */
  const detectMultipleMonitors = async () => {
    if (!isTabSwithingEnabled) {
      return {
        count: 1,
        screens: [],
        method: 'disabled',
        note: 'Screen proctoring disabled'
      };
    }
    try {
      // Method 1: Screen Details API (Chrome 93+)
      if ('getScreenDetails' in window) {
        try {
          const screenDetails = await window.getScreenDetails();
          return {
            count: screenDetails.screens.length,
            screens: screenDetails.screens.map(screen => ({
              width: screen.width,
              height: screen.height,
              isPrimary: screen.isPrimary,
              label: screen.label
            })),
            method: 'getScreenDetails'
          };
        } catch (permError) {
          console.warn("Screen Details API permission denied, trying fallback");
        }
      }
      
      // Method 2: isExtended property (Chrome 116+)
      if ('isExtended' in window.screen) {
        const isExtended = window.screen.isExtended;
        return {
          count: isExtended ? 2 : 1,
          screens: [{
            width: window.screen.width,
            height: window.screen.height,
            isPrimary: true
          }],
          method: 'isExtended',
          note: isExtended ? "Extended display detected" : "Single display"
        };
      }

      // Method 3: Fallback dimension analysis
      const totalWidth = window.screen.width;
      const suspiciousLayout = totalWidth > 3840; // Ultra-wide might indicate multi-monitor
      
      return {
        count: suspiciousLayout ? 2 : 1,
        screens: [{ width: totalWidth, height: window.screen.height }],
        method: 'dimension-analysis',
        note: "Using heuristics"
      };
      
    } catch (error) {
      console.error("Multi-monitor detection failed:", error);
      return {
        count: 1,
        screens: [],
        error: error.message
      };
    }
  };

  /**
   * Check for multiple monitors and update state
   */
  const checkForMultipleMonitors = async () => {
    if (!isTabSwithingEnabled) {
      return { count: 1, method: 'disabled' };
    }
    try {
      const screens = await detectMultipleMonitors();
      const hasMultiple = screens.count > 1;
      
      if (hasMultiple !== multipleMonitorsDetected) {
        setMultipleMonitorsDetected(hasMultiple);
        setMonitorCount(screens.count);
        
        if (hasMultiple) {
          // toast.error(
          //   `⚠️ Multiple monitors detected (${screens.count})! Please disconnect secondary displays.`,
          //   { 
          //     autoClose: false,
          //     toastId: 'multiple-monitors'
          //   }
          // );
          toast.error(
            `⚠️ Multiple monitors detected `,
            { 
              autoClose: true,
              toastId: 'multiple-monitors'
            }
          );
          console.error(`BLOCKED - ${screens.count} monitors detected`, screens);
        } else {
          toast.dismiss('multiple-monitors');
          // toast.success("✅ Single monitor confirmed");
          console.log("✓ Single monitor detected");
        }
      }
      
      return screens;
    } catch (error) {
      console.error("Monitor check failed:", error);
      return { count: 1, error: error.message };
    }
  };

  /**
   * Start continuous monitor detection
   */
  const startMonitorDetection = () => {
    if (!isTabSwithingEnabled) {
      console.log("Screen proctoring disabled - skipping monitor detection");
      return;
    }
    if (monitorCheckIntervalRef.current) return; // Already running
    
    monitorCheckIntervalRef.current = setInterval(async () => {
      await checkForMultipleMonitors();
    }, checkInterval);
  };

  /**
   * Stop monitor detection
   */
  const stopMonitorDetection = () => {
    if (monitorCheckIntervalRef.current) {
      clearInterval(monitorCheckIntervalRef.current);
      monitorCheckIntervalRef.current = null;
    }
  };

  /**
   * Request screen permission with validation
   */
  const requestScreenPermission = async () => {
    if (!isTabSwithingEnabled) {
      console.log("Screen proctoring disabled - skipping screen share request");
      return null;
    }

    console.log("STREAM MANAGER - Request for screen stream");
    
    // BLOCK if multiple monitors detected
    const screens = await detectMultipleMonitors();
    if (screens.count > 1) {
      // toast.error(
      //   `❌ Multiple monitors detected (${screens.count})! Please disconnect all secondary displays.`,
      //   { autoClose: false }
      // );
      toast.error(
        `Please disconnect any external monitor`,
        { autoClose: false }
      );
      console.error(`BLOCKED - ${screens.count} monitors detected`, screens);
      
      setMultipleMonitorsDetected(true);
      setMonitorCount(screens.count);
      
      throw new Error("Multiple monitors detected");
      // return null;
    }

    

    try {
      // Reuse existing stream if active
      const existingStream = activeStreamsRef.current.screen;
      if (
        existingStream &&
        existingStream.getTracks().length > 0 &&
        existingStream.active
      ) {
        console.log("STREAM MANAGER - Reusing existing screen stream");
        return existingStream;
      }

      console.log("STREAM MANAGER - Creating new screen stream");
    
      // Request screen share
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: 1, max: 5 },
          displaySurface: "monitor"
        },     
        audio: false,
        preferCurrentTab: false
      });

      // Validate what was shared
      const [videoTrack] = screenStream.getVideoTracks();
      const settings = videoTrack.getSettings();
      
      console.log("SCREEN SHARE SETTINGS:", settings);
      
      const displaySurface = settings.displaySurface;
      
      if (displaySurface !== "monitor") {
        toast.error(
          `You shared a "${displaySurface}". Please share your ENTIRE SCREEN.`,
          { autoClose: 8000 }
        );
        
        console.error(`VIOLATION - User shared ${displaySurface} instead of monitor`);
        screenStream.getTracks().forEach(track => track.stop());

        // return null;
        throw new Error(`Must share entire screen, not ${displaySurface}`);
      }
      
      // toast.success("✅ Entire screen sharing confirmed");
      console.log("✓ SCREEN SHARE VALIDATED");

      activeStreamsRef.current.screen = screenStream;
      screenStreamRef.current = screenStream;
      globalScreenStream = screenStream;

      // Detect when screen share stops
      videoTrack.onended = () => {
        console.warn("SCREEN STREAM ENDED - violation detected");
        activeStreamsRef.current.screen = null;
        screenStreamRef.current = null;
        setIsScreenSharing(false);
        globalScreenStream = null;
        globalIsScreenSharing = false; 
        toast.error("⚠️ Screen sharing stopped! This is a violation.", {
          autoClose: false
        });
        
        // Optional: trigger violation handlers
        // flagViolation("SCREEN_SHARE_STOPPED");
        // autoSubmitExam();
        return;
      };

      setIsScreenSharing(true);
      globalIsScreenSharing = true;
      setPermissionsStatus(prev => ({
        ...prev,
        screen: "granted"
      }));

      return screenStream;
    } catch (error) {
      console.error("STREAM MANAGER - Screen permission denied", error);
      
      setPermissionsStatus(prev => ({
        ...prev,
        screen: "denied"
      }));
      
      // toast.error("Screen sharing is required to continue.");
      // throw error;
      return null;
    }
  };

  /**
   * Stop screen sharing
   */
  const stopScreenSharing = () => {
    const stream = screenStreamRef.current || globalScreenStream;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      activeStreamsRef.current.screen = null;
      screenStreamRef.current = null;
      setIsScreenSharing(false);
      globalScreenStream = null; // ✅ Clear it
      globalIsScreenSharing = false; // ✅ Update module state
      console.log("Screen sharing stopped manually");
      globalScreenStream = null;
    }
  };

  /**
   * Validate screen share integrity
   */
  const validateScreenShareIntegrity = () => {
     if (!isTabSwithingEnabled) {
      return true;
    }

    const screenStream = screenStreamRef.current || globalScreenStream;
    
    if (!screenStream || !screenStream.active) {
      console.warn("INTEGRITY CHECK FAILED - No active screen stream");
      toast.error("Screen sharing is not active!");
      return false;
    }
    
    const [videoTrack] = screenStream.getVideoTracks();
    const settings = videoTrack.getSettings();
    
    if (settings.displaySurface !== "monitor") {
      console.error("INTEGRITY CHECK FAILED - Not sharing monitor");
      toast.error("You must share your entire screen!");
      
      screenStream.getTracks().forEach(track => track.stop());
      activeStreamsRef.current.screen = null;
      screenStreamRef.current = null;
      setIsScreenSharing(false);
      
      return false;
    }
    
    return true;
  };


  //function to capture screen shot
  const captureScreenshotBlob = async (options = {}) => {
    if (!isTabSwithingEnabled) {
      console.log("Screen proctoring disabled - skipping screenshot");
      return null;
    }

    const {
        format = 'png',
        quality = 0.92,
        width = null,
        height = null
    } = options;

     const stream = globalScreenStream || screenStreamRef.current;

    if (!stream || !stream.active) {
        console.log("No active screen");
        return null;
        // throw new Error("No active screen stream");
    }

    const [track] = stream.getVideoTracks();
    const imageCapture = new ImageCapture(track);
    const bitmap = await imageCapture.grabFrame();
    
    const canvas = document.createElement('canvas');
    canvas.width = width || bitmap.width;
    canvas.height = height || bitmap.height;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    
    const mimeType = `image/${format}`;
    const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, mimeType, quality);
    });
    console.log("Screen Cap")
    console.log({blob})
    // saveBlobToLocalFile(blob, "screen-capture.png");
    return blob;
 };

 const saveBlobToLocalFile = (blob, filename = `screenshot-${Date.now()}.png`) => {
  if (!blob) throw new Error("No blob to save");

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};


  useEffect(() => {
  if (autoStart && isTabSwithingEnabled) {
    checkForMultipleMonitors();
    startMonitorDetection();
  }
  
  // Sync screen sharing state continuously
  const syncInterval = setInterval(() => {
    if (globalScreenStream && !globalScreenStream.active) {
      console.log("🔄 Stream stopped externally - syncing state");
      globalScreenStream = null;
      globalIsScreenSharing = false;
      setIsScreenSharing(false);
      
      if (activeStreamsRef.current.screen) {
        activeStreamsRef.current.screen = null;
      }
      if (screenStreamRef.current) {
        screenStreamRef.current = null;
      }
    } else if (globalIsScreenSharing && !isScreenSharing) {
      // Global says sharing but local doesn't - sync up
      setIsScreenSharing(true);
    } else if (!globalIsScreenSharing && isScreenSharing) {
      // Global says not sharing but local does - sync down
      setIsScreenSharing(false);
    }
  }, 1000);
  
  return () => {
    clearInterval(syncInterval);
  };
}, [isScreenSharing, isTabSwithingEnabled]);

useEffect(() => {
  if (!isTabSwithingEnabled) return;
    
  // Initial sync on mount
  if (globalScreenStream && !globalScreenStream.active) {
    globalScreenStream = null;
    globalIsScreenSharing = false;
    toast.error("⚠️ Screen sharing was stopped!");
    setIsScreenSharing(false);
  } else if (globalScreenStream && globalScreenStream.active) {
    setIsScreenSharing(true);
  } else {
    setIsScreenSharing(false);
  }
}, [isTabSwithingEnabled]);


  return {
    // State
    multipleMonitorsDetected,
    monitorCount,
    permissionsStatus,
    isScreenSharing,
    canShareScreen: !multipleMonitorsDetected,
    isTabSwithingEnabled,
    
    // Functions
    requestScreenPermission,
    stopScreenSharing,
    checkForMultipleMonitors,
    startMonitorDetection,
    stopMonitorDetection,
    validateScreenShareIntegrity,
    detectMultipleMonitors,
    
    // Refs (for advanced usage)
    screenStreamRef,
    activeStreamsRef,

    // Screenshot capture
    captureScreenshotBlob
  };
};